import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/lib/supabaseClient';
import { registerScopeCache } from '@/lib/scopeCaches';
import {
  type UserRoleRow,
  heldUserRoleIds,
  legacyRoleCounts,
  resolvePermissions,
  resolveRoleIds,
} from '@/lib/rolePermissionResolution';
import { canMoveStageDirectly as resolveCanMoveStageDirectly } from '@/lib/stageMoveGate';
import { clearScopeCaches } from '@/lib/scopeCaches';
import {
  ACCESS_REVALIDATION_INTERVAL_MS,
  MY_ACCESS_STATE_RPC,
  decideAccessRevalidation,
  parseAccessState,
} from '@/lib/accessRevalidation';
import { AUTH_NOTICE_KEY } from '@/lib/accountStatus';
import { clearTenantSettingsCache } from '@/services/tenantSettingsService';

interface RoleInfo {
  id: number;
  name: string;
  global: boolean;
  tenant_id: string | null;
}

interface UserInfo {
  role_id: number;
  tenant_id: string;
  role: RoleInfo;
}

interface PermissionContextType {
  permissions: Set<string>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  userInfo: UserInfo | null;
  roleInfo: RoleInfo | null;
  /** PMV-1.5. Move a stage directly rather than request it. See `lib/stageMoveGate.ts`. */
  canMoveStageDirectly: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

type PermissionResult = {
  permissions: Set<string>;
  userInfo: UserInfo | null;
  roleInfo: RoleInfo | null;
  canMoveStageDirectly: boolean;
};

const PermissionContext = createContext<PermissionContextType>({
  permissions: new Set(),
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  userInfo: null,
  roleInfo: null,
  canMoveStageDirectly: false,
  loading: true,
  error: null,
  refetch: async () => {},
});

// Global cache for user permissions to prevent duplicate requests
const permissionCache = new Map<string, {
  permissions: Set<string>;
  userInfo: UserInfo | null;
  roleInfo: RoleInfo | null;
  canMoveStageDirectly: boolean;
  timestamp: number;
  promise?: Promise<any>;
}>();

// Cache expiration time (15 minutes as per plan)
const CACHE_EXPIRY = 15 * 60 * 1000;

/**
 * SUB-4.7 · FR74, AC2. Empty on any scope change.
 *
 * ⚠️ THE KEY BELOW LOOKS SCOPE-SAFE AND IS NOT. It is
 * `user-permissions-${user.id}-${currentTenant.tenant_id}`, and in merged scope
 * `currentTenant` is the ANCHOR, which for a tenant user is their own parent
 * account. So the key is BYTE-IDENTICAL in merged scope and in single-account
 * scope on the parent, and an entry written in one is served in the other for up
 * to fifteen minutes. Carrying a tenant id is what made it look handled.
 *
 * Registered rather than exported, because `TenantContext` is where a scope
 * change is detected and this module already imports `useTenant` from it. The
 * reasoning is in `lib/scopeCaches.ts`.
 */
registerScopeCache('PermissionContext.permissionCache', () => permissionCache.clear());

// Expose cache to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__permissionCache = {
    get: () => {
      const entries: any[] = [];
      permissionCache.forEach((value, key) => {
        entries.push({
          key,
          permissions: Array.from(value.permissions),
          roleInfo: value.roleInfo,
          userInfo: value.userInfo,
          age: `${Math.round((Date.now() - value.timestamp) / 1000)}s`,
          expired: Date.now() - value.timestamp >= CACHE_EXPIRY
        });
      });
      return entries;
    },
    clear: () => {
      permissionCache.clear();
    }
  };
}

// Tenant ID will be dynamically retrieved from TenantContext

// Exponential backoff for 522 errors
const retryWithBackoff = async function <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isLastAttempt = attempt === maxRetries - 1;
      const is522Error = error?.status === 522 || error?.code === 522;
      
      if (isLastAttempt || !is522Error) {
        throw error;
      }
      
      // Exponential backoff for 522 errors
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  
  throw new Error('Max retries exceeded');
}

// Deduplicated request function that returns permissions AND user/role info
const deduplicatedPermissionRequest = async (
  key: string,
  requestFn: () => Promise<PermissionResult>
): Promise<PermissionResult> => {
  const cached = permissionCache.get(key);


  // If we have a valid cached result, return it
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return {
      permissions: cached.permissions,
      userInfo: cached.userInfo,
      roleInfo: cached.roleInfo,
      canMoveStageDirectly: cached.canMoveStageDirectly
    };
  }

  // If there's an ongoing request, wait for it
  if (cached?.promise) {
    const result = await cached.promise;
    return result;
  }

  // Create new request
  const promise = requestFn();
  permissionCache.set(key, {
    permissions: new Set(),
    userInfo: null,
    roleInfo: null,
    canMoveStageDirectly: false,
    timestamp: Date.now(),
    promise
  });

  try {
    const result = await promise;

    // Only cache non-empty permissions or if we explicitly know this should be empty
    // For superadmin users, we should never cache empty permissions
    if (result.permissions.size > 0) {
      permissionCache.set(key, {
        permissions: result.permissions,
        userInfo: result.userInfo,
        roleInfo: result.roleInfo,
        canMoveStageDirectly: result.canMoveStageDirectly,
        timestamp: Date.now(),
        promise: undefined
      });
    } else {
      // Don't cache empty permissions - let them be fetched fresh each time
      permissionCache.delete(key);
    }

    return result;
  } catch (error) {
    // Remove failed request from cache
    permissionCache.delete(key);
    throw error;
  }
}

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [roleInfo, setRoleInfo] = useState<RoleInfo | null>(null);
  const [canMoveStageDirectly, setCanMoveStageDirectly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);

  // Memoized cache key to prevent unnecessary re-renders
  const cacheKey = useMemo(() => {
    if (!user?.id || !currentTenant?.tenant_id) return null;
    return `user-permissions-${user.id}-${currentTenant.tenant_id}`;
  }, [user?.id, currentTenant?.tenant_id]);

  const fetchUserPermissions = async () => {
    // Don't fetch if auth or tenant is still loading
    if (authLoading || tenantLoading || !user || !currentTenant || !cacheKey) {
      return;
    }

    // Check what's in the cache
    const cached = permissionCache.get(cacheKey);

    // Clear any potentially stale empty permissions from cache on fresh fetch
    if (cached && cached.permissions.size === 0 && !cached.promise) {
      permissionCache.delete(cacheKey);
    }

    // Prevent duplicate requests
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const result = await deduplicatedPermissionRequest(cacheKey, async () => {
        return await retryWithBackoff(async () => {
          // Get comprehensive user and role information
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select(`
              role_id,
              tenant_id,
              roles (
                id,
                name,
                global,
                tenant_id
              )
            `)
            .eq('id', user.id)
            .single();

          if (userError) {
            throw userError;
          }

          // Prepare user and role information for caching
          let cachedUserInfo: UserInfo | null = null;
          let cachedRoleInfo: RoleInfo | null = null;

          if (userData && userData.roles) {
            const roleData = Array.isArray(userData.roles) ? userData.roles[0] : userData.roles;

            cachedUserInfo = {
              role_id: userData.role_id,
              tenant_id: userData.tenant_id,
              role: {
                id: roleData.id,
                name: roleData.name,
                global: roleData.global,
                tenant_id: roleData.tenant_id
              }
            };

            cachedRoleInfo = {
              id: roleData.id,
              name: roleData.name,
              global: roleData.global,
              tenant_id: roleData.tenant_id
            };
          }

          // COM-R.155 · BOTH role systems, because the database reads both.
          //
          // `cost_holds_permission()` resolves `users.role_id` OR `user_roles`,
          // and COM-R.163 wired it into the three DEFINER gates. This used to be
          // an if/else — a legacy `role_id` meant `user_roles` was never even
          // queried — so the client hid controls the database was already
          // granting.
          //
          // Measured against live on 2026-08-26 over 471 active users: 8465
          // grants resolved before, 8722 after. 8 identities gained 257 grants;
          // ZERO grants moved the other way, and zero identities lost anything.
          // The change reveals authority, it does not confer it: every DEFINER
          // gate re-checks in the database regardless of what this resolves.
          //
          // The rule itself lives in `src/lib/rolePermissionResolution.ts` so it
          // is testable without a DOM or a Supabase client, the way
          // `mergedFilters.ts` and `tenantScope.ts` are. Its header carries the
          // reasoning, including why the hard-coded Superadmin list is gone and
          // why a short-circuit on Superadmin would have been a narrowing.
          const { data: userRolesData, error: userRolesError } = await supabase
            .from('user_roles')
            .select(`
              role_id,
              tenant_id,
              roles (
                id,
                name,
                global,
                tenant_id
              )
            `)
            .eq('user_id', user.id);

          if (userRolesError) {
            throw userRolesError;
          }

          // RBAC-7.1 · the account rule holds_permission_for() applies: a
          // user_roles row counts in its own account (global Superadmin
          // excepted), a role only when global or owned by this account. The
          // cache key is per account, so filtering here is safe.
          const heldRoleIds = heldUserRoleIds(userRolesData as UserRoleRow[] | null, currentTenant.tenant_id);
          const legacyRole = Array.isArray(userData?.roles) ? userData?.roles[0] : userData?.roles;

          const roleIds = resolveRoleIds({
            legacyRoleId: legacyRoleCounts(legacyRole, currentTenant.tenant_id) ? (userData?.role_id ?? null) : null,
            userRoleIds: heldRoleIds,
          });

          // An identity holding no role in either system. Not an error: it is
          // what a user stripped of their roles looks like, and it resolves to
          // no permissions rather than to a permissive default.
          if (roleIds.length === 0) {
            return {
              permissions: new Set<string>(),
              userInfo: cachedUserInfo,
              roleInfo: cachedRoleInfo,
              canMoveStageDirectly: false
            };
          }

          // Identities with no legacy `role_id` still need a role to describe
          // themselves with. Previously the `user_roles` branch supplied this;
          // it is preserved here rather than dropped.
          if (!cachedRoleInfo && userRolesData && userRolesData.length > 0) {
            const firstRow: any = userRolesData[0];
            const firstRole = Array.isArray(firstRow.roles) ? firstRow.roles[0] : firstRow.roles;
            if (firstRole) {
              cachedRoleInfo = {
                id: firstRole.id,
                name: firstRole.name,
                global: firstRole.global,
                tenant_id: firstRole.tenant_id
              };
              cachedUserInfo = {
                role_id: firstRow.role_id,
                tenant_id: currentTenant.tenant_id,
                role: cachedRoleInfo
              };
            }
          }

          // The account rule was applied to the role ids above (RBAC-7.1), so
          // every grant of a role that counts here is taken. role_permissions
          // carries no account of its own that the database consults.
          const { data: permissionData, error: permissionError } = await supabase
            .from('role_permissions')
            .select(`
              role_id,
              permissions!inner (
                name
              )
            `)
            .in('role_id', roleIds);

          if (permissionError) {
            throw permissionError;
          }

          const grants = (permissionData ?? []).flatMap((item: any) => {
            const permission = Array.isArray(item.permissions)
              ? item.permissions[0]?.name
              : item.permissions?.name;
            return permission ? [{ role_id: item.role_id, permission }] : [];
          });

          const permissionSet = resolvePermissions({
            legacyRoleId: userData?.role_id ?? null,
            userRoleIds: heldRoleIds,
            grants,
          });

          // PMV-1.5. Per role and tenant-scoped like the stage triggers, so the
          // union in `permissionSet` is deliberately not what decides this.
          const canMoveStageDirectly = resolveCanMoveStageDirectly({
            legacyRoleId: userData?.role_id ?? null,
            tenantRoleIds: (userRolesData ?? [])
              .filter((row: any) => row.tenant_id === currentTenant.tenant_id)
              .map((row: any) => row.role_id),
            grants,
          });

          return {
            permissions: permissionSet,
            userInfo: cachedUserInfo,
            roleInfo: cachedRoleInfo,
            canMoveStageDirectly
          };
        });
      });

      if (!signal.aborted) {
        setPermissions(result.permissions);
        setUserInfo(result.userInfo);
        setRoleInfo(result.roleInfo);
        setCanMoveStageDirectly(result.canMoveStageDirectly);
      }

    } catch (error: any) {
      if (!signal.aborted) {
        // Handle specific error types
        if (error?.status === 522) {
          setError('Connection timeout - please try again');
        } else if (error?.code === 'PGRST116') {
          setError('User not found');
        } else {
          setError('Failed to load user permissions');
        }
        
        // Keep empty permissions on error for security
        setPermissions(new Set());
        setCanMoveStageDirectly(false);
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // Only fetch permissions when auth and tenant are loaded AND we have a current tenant
    if (!authLoading && !tenantLoading && user && currentTenant && cacheKey) {
      fetchUserPermissions();
    } else if (!user) {
      // Clear permissions when user is not authenticated
      setPermissions(new Set());
      setCanMoveStageDirectly(false);
      setLoading(false);
      setError(null);
    } else if (!authLoading && !tenantLoading && user && !currentTenant) {
      // Auth and tenant loading are done, but no current tenant 
      // Give it a moment for the currentTenant state to update, then clear if still null
      const timeoutId = setTimeout(() => {
        // Double-check after a brief delay to handle React state update timing
        if (!currentTenant && !tenantLoading) {
          setPermissions(new Set());
          setCanMoveStageDirectly(false);
          setLoading(false);
          setError('No tenant access');
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    } else {
      // IMPORTANT: Keep loading=true while waiting for conditions to be met
      if (!loading) {
        setLoading(true);
      }
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isFetchingRef.current = false;
    };
  }, [user?.id, currentTenant?.tenant_id, authLoading, tenantLoading, cacheKey]);

  // Memoized permission checking functions for O(1) performance
  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      return permissions.has(permission);
    };
  }, [permissions]);

  const hasAnyPermission = useMemo(() => {
    return (permissionList: string[]): boolean => {
      return permissionList.some((permission) => permissions.has(permission));
    };
  }, [permissions]);

  const hasAllPermissions = useMemo(() => {
    return (permissionList: string[]): boolean => {
      return permissionList.every((permission) => permissions.has(permission));
    };
  }, [permissions]);

  const refetch = async () => {
    // Clear cache for this user
    if (cacheKey) {
      permissionCache.delete(cacheKey);
    }
    await fetchUserPermissions();
  };

  /**
   * RBAC-4.3 · NFR-10. Without this the answer fetched above stands for the life of the page: the
   * fifteen-minute expiry is only consulted when something refetches, and nothing did. Rules and the
   * reasoning are in `lib/accessRevalidation.ts`. ⚠️ The database is the boundary, not this.
   */
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const fingerprintRef = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    fingerprintRef.current = null;
    let cancelled = false;
    let inFlight = false;

    const ask = async () => {
      if (cancelled || inFlight || document.visibilityState !== 'visible') return;
      inFlight = true;
      try {
        const { data, error } = await supabase.rpc(MY_ACCESS_STATE_RPC);
        if (cancelled) return;
        const decision = decideAccessRevalidation(fingerprintRef.current, error ? null : parseAccessState(data));
        fingerprintRef.current = decision.fingerprint;

        if (decision.action === 'refetch') {
          clearTenantSettingsCache();
          await refetchRef.current();
        } else if (decision.action === 'sign_out') {
          clearScopeCaches();
          clearTenantSettingsCache();
          try {
            sessionStorage.setItem(AUTH_NOTICE_KEY, decision.reason);
          } catch {
            // Private browsing or a quota. The sign-out still has to happen.
          }
          // Local: the server already deleted every session of theirs (SUB-1.37).
          await supabase.auth.signOut({ scope: 'local' });
        }
      } catch {
        // Unreachable is not an answer; see decideAccessRevalidation.
      } finally {
        inFlight = false;
      }
    };

    void ask();
    const onFocus = () => { void ask(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void ask();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => { void ask(); }, ACCESS_REVALIDATION_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [user?.id, authLoading]);

  const contextValue: PermissionContextType = useMemo(() => ({
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userInfo,
    roleInfo,
    canMoveStageDirectly,
    loading,
    error,
    refetch,
  }), [permissions, hasPermission, hasAnyPermission, hasAllPermissions, userInfo, roleInfo, canMoveStageDirectly, loading, error, refetch]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

// Convenience hooks for common permission patterns
export function useHasPermission(permission: string) {
  const { hasPermission, loading } = usePermissions();
  return { hasPermission: hasPermission(permission), loading };
}

export function useHasAnyPermission(permissions: string[]) {
  const { hasAnyPermission, loading } = usePermissions();
  return { hasPermission: hasAnyPermission(permissions), loading };
}

export function useHasAllPermissions(permissions: string[]) {
  const { hasAllPermissions, loading } = usePermissions();
  return { hasPermission: hasAllPermissions(permissions), loading };
}
